import { useQuery } from '@tanstack/react-query'
import { Download, FileSpreadsheet } from 'lucide-react'
import { useState } from 'react'
import { AButton, Callout, Section } from './Section'
import { download, get } from '../../lib/api'

/** Slide 23's "Export to Excel": CSV files, opened directly by Excel. */

const DESCRIPTIONS: Record<string, string> = {
  students:
    'Every registration with booths visited, draw eligibility and certificate status. The Registrar’s copy.',
  scans: 'One row per booth scan, with the time, booth, student and whether it was QR or typed.',
  draws:
    'Every lucky draw winner with their position, phone and e-mail — the prize table’s sheet. Re-drawn positions name who was not present.',
  results: 'Final standings for every judged award, with each judge’s total side by side.',
  marking: 'Every mark by every judge on every KPI — 693 rows. The audit copy behind the rankings.',
  certificates: 'Certificates issued, delivery status and the public verification link for each.',
}

export function ExportsSection() {
  const [busy, setBusy] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const exports = useQuery({
    queryKey: ['admin-exports'],
    queryFn: () => get<{ exports: { key: string; label: string }[] }>('/api/admin/exports', 'staff'),
  })

  async function run(key: string) {
    setBusy(key)
    setError(null)
    try {
      const day = new Date().toISOString().slice(0, 10)
      await download(`/api/admin/export/${key}`, `SDG2026_${key}_${day}.csv`, 'staff')
    } catch {
      setError('That export failed. Try again, or check the server log.')
    } finally {
      setBusy(null)
    }
  }

  return (
    <Section title="Exports" description="CSV files, ready to open in Excel. Every download is recorded in the audit log.">
      <div className="space-y-3">
        {error && <Callout tone="error">{error}</Callout>}

        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
          {(exports.data?.exports ?? []).map((item) => (
            <div
              key={item.key}
              className="flex flex-col rounded-2xl border border-white bg-white/95 p-4 shadow-lg shadow-blue-900/5"
            >
              <div className="flex items-start gap-3">
                <span className="grid size-11 shrink-0 place-items-center rounded-xl bg-emerald-100 text-emerald-600">
                  <FileSpreadsheet className="size-5" strokeWidth={2.2} />
                </span>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-bold leading-tight text-[#0B1E3F]">{item.label}</p>
                  <p className="mt-1 text-xs leading-snug text-slate-500">{DESCRIPTIONS[item.key] ?? ''}</p>
                </div>
              </div>

              <AButton tone="navy" block className="mt-4" disabled={busy != null} onClick={() => run(item.key)}>
                <Download className="size-4" strokeWidth={2.2} />
                {busy === item.key ? 'Preparing…' : 'Download CSV'}
              </AButton>
            </div>
          ))}
        </div>

        <Callout>
          Files are UTF-8 with a byte-order mark and CRLF line endings, so Excel on Windows opens them with
          accented names intact and every row on its own line.
        </Callout>
      </div>
    </Section>
  )
}
